import React from "react";
import {FormattedMessage, useIntl} from "react-intl";
import Loader from "../../Utils/Loader";
import classes from "./styles.module.css";
import Empty from "./Empty";

// Status pill
function StatusPill({isBlocked}) {
    const text = isBlocked ? (
        <FormattedMessage id="AntiFraudList.status.blocked" defaultMessage="Заблокирован"/>
    ) : (
        <FormattedMessage id="AntiFraudList.status.unblocked" defaultMessage="Разблокирован"/>
    );
    const bg = isBlocked ? "#fde8e8" : "#e8f8ef";
    const color = isBlocked ? "#a43232" : "#1b8356";

    return (
        <span style={{padding: "2px 8px", borderRadius: 12, background: bg, color, fontSize: 12}}>
            {text}
        </span>
    );
}

// Source label
function SourceLabel({source, reference}) {
    const sourceItem = (reference.sources || []).find((s) => s.value === source);
    const label = sourceItem ? sourceItem.label : source;

    const colors = {
        tkb: {bg: "#e3f2fd", color: "#1565c0"},
        finstar: {bg: "#fff3e0", color: "#e65100"},
        scanfraud: {bg: "#f3e5f5", color: "#7b1fa2"},
        nx: {bg: "#e8eaf6", color: "#3949ab"},
        manual: {bg: "#fce4ec", color: "#c2185b"},
    };

    const style = colors[source] || {bg: "#f5f5f5", color: "#666"};

    return (
        <span style={{padding: "2px 8px", borderRadius: 8, background: style.bg, color: style.color, fontSize: 11}}>
            {label}
        </span>
    );
}

// Kind label
function KindLabel({kind, reference}) {
    const kindItem = (reference.kinds || []).find((k) => k.value === kind);
    const label = kindItem ? kindItem.label : kind;

    return (
        <span style={{fontSize: 12, opacity: 0.8}}>
            {label}
        </span>
    );
}

function formatDate(dateStr, locale) {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return d.toLocaleDateString(locale) + " " + d.toLocaleTimeString(locale, {
        hour: "2-digit",
        minute: "2-digit"
    });
}

function TableRow({row, isActive, onClick, reference, locale}) {
    const trClass = isActive ? `${classes.Row} ${classes.Active}` : classes.Row;

    return (
        <tr className={trClass} onClick={() => onClick(row)} style={{cursor: "pointer"}}>
            <td className={classes.CellCenter}>#{row.id}</td>
            <td className={classes.Cell}>
                <SourceLabel source={row.source} reference={reference}/>
            </td>
            <td className={classes.Cell}>
                <KindLabel kind={row.kind} reference={reference}/>
            </td>
            <td className={classes.Cell}>
                <div style={{fontFamily: "monospace", fontSize: 13}}>{row.valueMasked || "—"}</div>
            </td>
            <td className={classes.Cell}>
                <div style={{fontSize: 12}}>{row.reasonMessage || row.reasonCode || "—"}</div>
            </td>
            <td className={classes.CellCenter}>
                <span style={{fontWeight: 600}}>{row.hits || 0}</span>
            </td>
            <td className={classes.Cell}>
                <div style={{fontSize: 11, opacity: 0.7}}>{formatDate(row.lastSeenAt, locale)}</div>
            </td>
            <td className={classes.CellCenter}>
                <StatusPill isBlocked={row.isBlocked}/>
            </td>
        </tr>
    );
}

function MobileCard({row, isActive, onClick, reference, locale}) {
    const cardClass = isActive ? `${classes.MobileCard} ${classes.MobileCardActive}` : classes.MobileCard;

    return (
        <button type="button" className={cardClass} onClick={() => onClick(row)}>
            <div className={classes.MobileRow}>
                <div className={classes.MobilePrimary}>#{row.id}</div>
                <StatusPill isBlocked={row.isBlocked}/>
            </div>
            <div className={classes.MobileRow}>
                <SourceLabel source={row.source} reference={reference}/>
                <KindLabel kind={row.kind} reference={reference}/>
            </div>
            <div className={classes.MobileValue}>{row.valueMasked || "—"}</div>
            {(row.reasonMessage || row.reasonCode) && (
                <div className={classes.MobileReason}>{row.reasonMessage || row.reasonCode}</div>
            )}
            <div className={classes.MobileFooter}>
                <span>
                    <FormattedMessage id="AntiFraudList.table.hits" defaultMessage="Попадания" />: {row.hits || 0}
                </span>
                <span>{formatDate(row.lastSeenAt, locale)}</span>
            </div>
        </button>
    );
}

function AntiFraudList({data, currentId, isLoading, onSelect, reference}) {
    const intl = useIntl();
    const rows = Array.isArray(data) ? data : [];

    if (isLoading) return <Loader/>;
    if (!rows.length) return <Empty/>;

    return (
        <React.Fragment>
            <div className={classes.TableScroll}>
                <table className={classes.Table}>
                    <thead>
                    <tr>
                        <th className={classes.HeaderCell}>
                            <FormattedMessage id="AntiFraudList.table.id" defaultMessage="Идентификатор"/>
                        </th>
                        <th className={classes.HeaderCell}>
                            <FormattedMessage id="AntiFraudList.table.source" defaultMessage="Источник"/>
                        </th>
                        <th className={classes.HeaderCell}>
                            <FormattedMessage id="AntiFraudList.table.kind" defaultMessage="Тип"/>
                        </th>
                        <th className={classes.HeaderCell}>
                            <FormattedMessage id="AntiFraudList.table.value" defaultMessage="Значение"/>
                        </th>
                        <th className={classes.HeaderCell}>
                            <FormattedMessage id="AntiFraudList.table.reason" defaultMessage="Причина"/>
                        </th>
                        <th className={classes.HeaderCell}>
                            <FormattedMessage id="AntiFraudList.table.hits" defaultMessage="Попадания"/>
                        </th>
                        <th className={classes.HeaderCell}>
                            <FormattedMessage id="AntiFraudList.table.lastSeen" defaultMessage="Последний раз"/>
                        </th>
                        <th className={classes.HeaderCell}>
                            <FormattedMessage id="AntiFraudList.table.status" defaultMessage="Статус"/>
                        </th>
                    </tr>
                    </thead>
                    <tbody>
                    {rows.map((row) => (
                        <TableRow
                            key={row.id}
                            row={row}
                            isActive={currentId === row.id}
                            onClick={onSelect}
                            reference={reference}
                            locale={intl.locale}
                        />
                    ))}
                    </tbody>
                </table>
            </div>

            <div className={classes.MobileList}>
                {rows.map((row) => (
                    <MobileCard
                        key={row.id}
                        row={row}
                        isActive={currentId === row.id}
                        onClick={onSelect}
                        reference={reference}
                        locale={intl.locale}
                    />
                ))}
            </div>
        </React.Fragment>
    );
}

AntiFraudList.defaultProps = {
    data: [],
    isLoading: false,
    onSelect: () => {
    },
    reference: {sources: [], kinds: []},
};

export default AntiFraudList;



// WEBPACK FOOTER //
// ./src/Components/ComponentsOfAdministrator/AntiFraudList/index.js